import { MailFilled, PhoneFilled } from '@ant-design/icons';
import { Button, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

const { Text, Title } = Typography;

/**
 * The failure dialog for the advisor's final submit, in two variants.
 *
 * Deliberately a modal and not a toast. The copy BMO specified ends in a support email and a
 * phone number, and an antd `message` auto-dismisses in about three seconds — long enough to
 * register that something broke, nowhere near long enough to copy a phone number down. So this
 * stays on screen until the advisor dismisses it.
 *
 * Dismissible, unlike its sibling `ProcessingModal`: nothing is in flight behind it. The chain
 * has already settled and failed, the advisor's answers and files are intact underneath, and
 * closing it puts them back on the step with Submit live again — which is what "Please try
 * again" is telling them to do.
 *
 * <p><b>`session` is not a lesser `technical`.</b> It is shown when BMO would not take the
 * advisor's ISAM token, which is not a fault anyone can help with — the fix is to sign in
 * again. Giving that advisor a support phone number wastes their time and BMO's, and it is the
 * same reasoning that stops the BFF raising a support incident for the same codes. One
 * component with a variant rather than two, so the pair cannot drift into disagreeing about
 * which failures are which.</p>
 *
 * Which variant a failure gets is `classifySubmitFailure`'s decision, and it explains itself.
 */
export function SubmitErrorModal({
  onClose,
  variant = 'technical',
}: {
  onClose: () => void;
  variant?: 'technical' | 'session';
}) {
  const { t } = useTranslation();
  if (variant === 'session') {
    return (
      <Modal
        open
        onCancel={onClose}
        closable
        maskClosable={false}
        title={null}
        centered
        width={480}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="primary" onClick={onClose}>
              {t('formRenderer.submitError.dismiss')}
            </Button>
          </div>
        }
        aria-labelledby="submit-session-modal-title"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '12px 4px 4px' }}>
          <Title id="submit-session-modal-title" level={4} style={{ margin: 0, color: '#111827' }}>
            {t('formRenderer.sessionError.title')}
          </Title>
          {/* No support contact, on purpose — see the component remarks. */}
          <Text style={{ fontSize: 14, color: '#374151' }}>
            {t('formRenderer.sessionError.body')}
          </Text>
        </div>
      </Modal>
    );
  }
  return (
    <Modal
      open
      onCancel={onClose}
      // Escape and the × both just close it — see the note above on why this one is escapable
      // where ProcessingModal is not.
      closable
      maskClosable={false}
      title={null}
      centered
      width={480}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="primary" onClick={onClose}>
            {t('formRenderer.submitError.dismiss')}
          </Button>
        </div>
      }
      // The copy IS the content, so the dialog is labelled by its own heading rather than by a
      // title bar it does not have. Same treatment as ProcessingModal.
      aria-labelledby="submit-error-modal-title"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '12px 4px 4px' }}>
        <Title id="submit-error-modal-title" level={4} style={{ margin: 0, color: '#991b1b' }}>
          {t('formRenderer.submitError.title')}
        </Title>
        <Text style={{ fontSize: 14, color: '#374151' }}>{t('formRenderer.submitError.body')}</Text>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Live mailto/tel targets, not plain text. An advisor on a laptop clicks through to
              their mail client; one on a tablet dials. The visible label stays the raw address
              and number so it can also simply be read out or written down. */}
          <a
            href={`mailto:${t('formRenderer.submitError.email')}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14 }}
          >
            <MailFilled aria-hidden style={{ color: '#4b5563' }} />
            {t('formRenderer.submitError.email')}
          </a>
          <a
            // The FR copy spaces the area code ("416 347-1978"); tel: wants digits only, so the
            // href is built from the label rather than translated as a second string that could
            // drift away from it.
            href={`tel:${t('formRenderer.submitError.phone').replace(/[^\d+]/g, '')}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14 }}
          >
            <PhoneFilled aria-hidden style={{ color: '#4b5563' }} />
            {t('formRenderer.submitError.phone')}
          </a>
        </div>
      </div>
    </Modal>
  );
}
